import type { Comment, PostComment } from "@theta/api-client";

/**
 * ============================================================================
 * ONE THREAD, ONE SHAPE — the placement rules a comment cache needs
 * ============================================================================
 *
 * A post's comments arrive by two routes that disagree about their own shape,
 * and used to be reconciled by four pieces of component-local state
 * (`localComments`, `locallyAddedIds`, `localReplyCount` in `PostCard`, and the
 * passed-versus-fetched merge in `CommentItem`). Every one of them existed
 * because the query layer did not hold the answer. This file is the answer, so
 * they are gone:
 *
 *  - `GET /posts/{id}/comments?depth=all` answers `Comment[]`, DEPTH-FIRST,
 *    with `parentId` nulled at the top of what was asked for and a `depth` on
 *    every row. That is the canonical shape, and it is what
 *    `["v1","comments", postId]` holds.
 *  - A feed entry embeds `PostComment[]` — a BOUNDED preview of the same rows
 *    (`FEED_COMMENT_LIMIT`, newest, closed over ancestors) without `depth` or
 *    `isHidden`. `seedThread` converts one into the other so the feed can paint
 *    a card without a second request.
 *  - `POST /posts/{id}/comments` answers the created `Comment`, read back
 *    through the thread's own assembly — but with `parentId: null` and
 *    `depth: 0` REGARDLESS of where it sits, because the route serialises it
 *    as the root of what was asked for (`findThreadComment(…, parent.id, 0)`).
 *    So the client, which knows the id it posted to, does the placing:
 *    `placeInThread`.
 */

/** The id a comment was POSTed to: the post for a comment, a comment for a reply. */
type ParentId = number;

/**
 * The feed's embedded comments, in the thread read's shape.
 *
 * ONE DIFFERENCE TO CLOSE: the preview has no `depth`. It is ancestor-closed
 * and chronological, which is exactly what makes depth derivable here in one
 * pass — a row's parent has always been seen already.
 *
 * A ROW WHOSE PARENT IS NOT IN THE LIST IS A ROOT OF THIS LIST, and that is a
 * real case rather than defensive coding: `commentPreviewsFor` reports the
 * nearest VISIBLE ancestor as a reply's parent, so a reply under a deleted
 * comment arrives pointing at something further up or at nothing. Reading it
 * as a root is what `toBranches` does with it anyway, and it keeps `depth`
 * from counting a level that is not on screen.
 */
export function seedThread(preview: PostComment[]): Comment[] {
  const depths = new Map<number, number>();
  return preview.map((row) => {
    const parentDepth = row.parentId == null ? undefined : depths.get(row.parentId as number);
    const depth = parentDepth === undefined ? 0 : parentDepth + 1;
    depths.set(row.id, depth);
    return {
      ...row,
      parentId: parentDepth === undefined ? null : row.parentId,
      depth,
      /**
       * `isHidden` NOW RIDES ON THE ROW and is spread above rather than
       * overridden here. It used to be forced to `false` on the argument that
       * the preview's walk filters hidden rows — true until OSO-582 let a
       * held comment into a feed card for its author and for a moderator, and
       * a seed that flattened it would have dropped the "Skjult" mark the
       * moment the thread opened.
       *
       * `state` IS forced, and stays forced: only `threadPage` keeps a deleted
       * comment as a tombstone (OSO-583). A feed preview re-parents the
       * replies to the nearest ancestor it can still show and drops the row,
       * so a row that is IN a preview is one that was not deleted.
       */
      state: "comment",
    };
  });
}

/**
 * Splice one comment into a depth-first thread, where the server would have
 * put it: last child of its parent, after everything already nested under it.
 *
 * `postedTo` is the id the comment was written against, NOT `created.parentId`
 * — see the header. A top-level comment appends at the end, which is where the
 * next thread read will put it too: the walk orders siblings by id and this one
 * has the highest.
 *
 * Idempotent by id, so a write-through landing on top of a poll that already
 * carried the row replaces it rather than doubling it.
 */
export function placeInThread(
  thread: Comment[],
  comment: Comment,
  postedTo: ParentId,
  rootPostId: number,
): Comment[] {
  const rest = thread.filter((row) => row.id !== comment.id);

  if (postedTo === rootPostId) {
    return [...rest, { ...comment, parentId: null, depth: 0 }];
  }

  const parentIndex = rest.findIndex((row) => row.id === postedTo);
  // The parent is not in this thread — a reply written against a comment the
  // bounded preview did not carry. Dropping it is right: the row belongs
  // somewhere this list cannot express, and the next full read has both.
  if (parentIndex === -1) return rest;

  const parent = rest[parentIndex]!;
  let after = parentIndex + 1;
  while (after < rest.length && rest[after]!.depth > parent.depth) after++;

  const placed: Comment = {
    ...comment,
    parentId: postedTo as Comment["parentId"],
    depth: parent.depth + 1,
  };
  return [...rest.slice(0, after), placed, ...rest.slice(after)];
}

/**
 * ---------------------------------------------------------------------------
 * WHAT THE FEED'S SNAPSHOT IS NOT ALLOWED TO UNDO
 * ---------------------------------------------------------------------------
 *
 * The feed republishes its embedded comments into the thread cache on every
 * tick, so a card the reader is not interacting with stays as fresh as it is
 * today. That snapshot is authoritative for everything it contains — and blind
 * to a comment written in the ~1 RTT since it was requested. Replacing the
 * cache with it would make your own just-written comment blink out and come
 * back on the next tick.
 *
 * So a write records its row here, and `mergeSeed` carries that row across any
 * snapshot too old to know about it. The ledger is not a cache and does not
 * expire on a timer: an id is forgotten the moment a snapshot echoes it, which
 * is the only evidence that mattered.
 */
const pendingLocal = new Map<number, Set<number>>();

export function rememberLocalComment(rootPostId: number, commentId: number): void {
  const ids = pendingLocal.get(rootPostId) ?? new Set<number>();
  ids.add(commentId);
  pendingLocal.set(rootPostId, ids);
}

/**
 * ---------------------------------------------------------------------------
 * A PREVIEW MAY NOT TRUNCATE A THREAD THAT WAS READ IN FULL
 * ---------------------------------------------------------------------------
 *
 * `PostCard.comments` is bounded: at most `FEED_COMMENT_LIMIT` (10), the
 * newest, closed over ancestors. That is the right payload for painting a card
 * and the wrong one for overwriting a thread somebody has opened — a
 * 40-comment discussion read in full would snap back to its last ten on the
 * next feed tick, which is a worse bug than the one the republish prevents.
 *
 * So a full read marks its thread, and from then on a snapshot may UPDATE the
 * rows it carries and ADD ones the thread lacks, but never remove. The mark
 * dies with the cache entry, through the same eviction hook as the ledger
 * below — an entry that has been collected has not been read in full any more,
 * and the card that re-seeds it is back to taking the snapshot whole,
 * deletions included.
 */
const completeThreads = new Set<number>();

export function markThreadComplete(rootPostId: number): void {
  completeThreads.add(rootPostId);
}

/**
 * Everything remembered about one thread, dropped together.
 *
 * Wired to the query cache's `removed` event (`useComments`), because that is
 * the only moment either fact stops being true: the rows this ledger protects
 * are gone, so protecting them protects nothing. Without it the ledger grows
 * for the life of the tab — the post page has no seed, so nothing there ever
 * echoes an id back — and, worse, an id that NEVER reaches a snapshot (deleted
 * before the feed's next tick, held for approval) would keep re-injecting its
 * stale row into every snapshot forever.
 */
export function forgetThread(rootPostId: number): void {
  pendingLocal.delete(rootPostId);
  completeThreads.delete(rootPostId);
}

/**
 * How many of these rows the server has not counted yet.
 *
 * `PostCard.replyCount` is the whole thread's total and the list beside it may
 * be a bounded preview, so a card whose post has more comments than the
 * preview carries cannot see its own new comment in either number. This is the
 * difference between them.
 */
export function pendingLocalCount(rootPostId: number, thread: Comment[]): number {
  const pending = pendingLocal.get(rootPostId);
  if (!pending || pending.size === 0) return 0;
  return thread.reduce((n, row) => (pending.has(row.id as number) ? n + 1 : n), 0);
}

/** A row that is gone rather than pending: a delete, a hide, a rolled-back write. */
export function dropLocalComment(rootPostId: number, commentId: number): void {
  pendingLocal.get(rootPostId)?.delete(commentId);
}

/**
 * The thread a fetch just produced, with any local row it does not know about
 * put back — and every id it DOES carry forgotten, because the server echoing
 * a row is the whole of what the ledger was waiting for.
 *
 * `cancelQueries` in `useCreateComment` is not enough on its own: the 1 Hz
 * poll starts a new fetch as soon as the previous one settles, so a response
 * can land between `onMutate` and `onSuccess` and replace the cache wholesale.
 * Without this the placeholder blinks out mid-flight on every comment that
 * takes longer than a tick to write.
 */
export function applyPendingLocal(
  fetched: Comment[],
  cached: Comment[] | undefined,
  rootPostId: number,
): Comment[] {
  const pending = pendingLocal.get(rootPostId);
  if (!pending || pending.size === 0) return fetched;

  const fetchedIds = new Set(fetched.map((row) => row.id as number));
  for (const id of fetchedIds) pending.delete(id);
  if (pending.size === 0 || !cached) return fetched;

  let merged = fetched;
  for (const row of cached) {
    const id = row.id as number;
    if (!pending.has(id)) continue;
    merged = placeInThread(merged, row, (row.parentId as number) ?? rootPostId, rootPostId);
  }
  return merged;
}

/** Test seam. Nothing in the app clears the whole ledger. */
export function __resetLocalComments(): void {
  pendingLocal.clear();
  completeThreads.clear();
}

/**
 * A thread read in full, refreshed by a preview of its last ten.
 *
 * ONLY THE VOLATILE FIELDS TRAVEL. Swapping the whole row would import the
 * preview's own `depth`, `parentId` and `isHidden`, and those are not the same
 * facts the full read produced: `commentPreviewsFor` reports the nearest
 * VISIBLE ancestor as a reply's parent, `seedThread` reads any row whose
 * parent is missing as a root, and both hardcode `isHidden: false`. Take them
 * and a reply re-parented by a moderator's hide renders as a sibling of a
 * parent that is still on screen — and `placeInThread`'s child-span scan then
 * mis-inserts everything written under that branch afterwards. Shape is the
 * full read's; content is the newest snapshot's.
 */
function refreshWithoutRemoving(
  cached: Comment[],
  seed: Comment[],
  rootPostId: number,
): Comment[] {
  const fresh = new Map(seed.map((row) => [row.id as number, row]));
  const kept = cached.map((row) => {
    const update = fresh.get(row.id as number);
    return update
      ? { ...update, parentId: row.parentId, depth: row.depth, isHidden: row.isHidden }
      : row;
  });

  const known = new Set(kept.map((row) => row.id as number));
  let merged = kept;
  for (const row of seed) {
    if (known.has(row.id as number)) continue;
    merged = placeInThread(merged, row, (row.parentId as number) ?? rootPostId, rootPostId);
  }
  return merged;
}

/**
 * The feed's snapshot, plus the local rows it has not caught up with yet.
 *
 * Order comes from `placeInThread`, so a carried row lands under its parent
 * rather than at the end — a reply written while the feed was mid-request does
 * not jump to the bottom of the thread and then hop back one tick later.
 */
export function mergeSeed(
  cached: Comment[] | undefined,
  seed: Comment[],
  rootPostId: number,
): Comment[] {
  if (!cached || cached.length === 0) return seed;

  const seedIds = new Set(seed.map((row) => row.id as number));
  const pending = pendingLocal.get(rootPostId);
  // The server has echoed these. Nothing to protect any more.
  for (const id of seedIds) pending?.delete(id);

  if (completeThreads.has(rootPostId)) {
    return refreshWithoutRemoving(cached, seed, rootPostId);
  }
  if (!pending || pending.size === 0) return seed;

  let merged = seed;
  for (const row of cached) {
    const id = row.id as number;
    if (seedIds.has(id) || !pending.has(id)) continue;
    merged = placeInThread(merged, row, (row.parentId as number) ?? rootPostId, rootPostId);
  }
  return merged;
}
